import { usNflPlayerFamily, usNflPlayerMetadata, type UsNflPlayerMetadata } from './nflPlayerFamily.js';

const family = <S extends string, K extends 'threshold' | 'binary'>(stat: S, kind: K) =>
  Object.freeze({ stat, kind, period: 'full' as const });

/** Venue's documented full-game NBA families. Core's stat spelling is kept;
 * no Kalshi alias, player-name join or native strike conversion is implied. */
export const US_NBA_PLAYER_FAMILIES = Object.freeze({
  basketball_player_points: family('points', 'threshold'),
  basketball_player_assists: family('assists', 'threshold'),
  basketball_player_rebounds: family('rebounds', 'threshold'),
  basketball_player_threes: family('threes', 'threshold'),
  basketball_player_steals: family('steals', 'threshold'),
  basketball_player_blocks: family('blocks', 'threshold'),
  basketball_player_double_double: family('double-double', 'binary'),
  basketball_player_triple_double: family('triple-double', 'binary'),
});
type NbaFamily = typeof US_NBA_PLAYER_FAMILIES[keyof typeof US_NBA_PLAYER_FAMILIES];
export type UsPlayerMetadata = Omit<UsNflPlayerMetadata, 'stat'> & { stat: UsNflPlayerMetadata['stat'] | NbaFamily['stat'] };

export function usPlayerFamily(value: unknown, sport: unknown): ReturnType<typeof usNflPlayerFamily> | NbaFamily | null {
  if (sport === 'NFL') return usNflPlayerFamily(value);
  return sport === 'NBA' && typeof value === 'string' && Object.prototype.hasOwnProperty.call(US_NBA_PLAYER_FAMILIES, value)
    ? US_NBA_PLAYER_FAMILIES[value as keyof typeof US_NBA_PLAYER_FAMILIES] : null;
}

export function usPlayerMetadata(type: unknown, value: unknown, gameKey: string): UsPlayerMetadata | null {
  if (gameKey.startsWith('nfl|')) return usNflPlayerMetadata(type, value, gameKey);
  const f = usPlayerFamily(type, 'NBA');
  if (!f || !gameKey.startsWith('nba|') || !value || typeof value !== 'object' || Array.isArray(value)) return null;
  const r = value as Record<string, unknown>;
  if (typeof r.player !== 'string' || !r.player.trim() || r.player.length > 256
    || /[\u0000-\u001f\u007f]/.test(r.player) || r.stat !== f.stat
    || (r.kind !== undefined && r.kind !== f.kind) || (r.period != null && r.period !== f.period)
    || (r.playerTeam != null && (typeof r.playerTeam !== 'string' || !gameKey.split('|').slice(3, 5).includes(r.playerTeam)))) return null;
  return { player: r.player.trim(), playerTeam: r.playerTeam == null ? null : String(r.playerTeam),
    stat: f.stat, kind: f.kind, period: f.period };
}
